import Big from "big.js";
import { useAppStore } from "@/stores/app";

export default class useMisc {
  static audioExtensions = [
    ".mp3",
    ".aac",
    ".m4a",
    ".wav",
    ".ogg",
    ".wma",
    ".mp2"
  ];
  static videoExtensions = [
    ".3g2",
    ".3gp",
    ".asf",
    ".wmv",
    ".avi",
    ".f4v",
    ".flv",
    ".imf",
    ".mkv",
    ".mov",
    ".mp4",
    ".mpeg1",
    ".mpg",
    ".mpeg",
    ".mpeg2",
    ".mxf",
    ".webm",
    ".mkv"
  ];
  static imageExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".bmp",
    ".webp",
    ".svg"
  ];

  static clone(obj) {
    return JSON.parse(JSON.stringify(obj));
  }

  // Escape HTML-significant chars so a string is safe to inject via v-html.
  static escapeHtml(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // Render `text` with an optional search `term` wrapped in <mark>, safe for
  // v-html. The text is HTML-escaped FIRST (so any markup in the data — e.g. an
  // AI transcript/subtitle — is inert), then matches of the escaped term are
  // wrapped in <mark>. The term is regex- and HTML-escaped so it can't break
  // out. The only live HTML in the result is our own <mark> tags. (CIR-3267)
  static highlightSafe(text, term) {
    const safeText = this.escapeHtml(text);
    if (!term) return safeText;
    const safeTerm = this.escapeHtml(term).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    if (!safeTerm) return safeText;
    return safeText.replace(new RegExp(`(${safeTerm})`, "gi"), "<mark>$1</mark>");
  }

  static copyToClipboard(str) {
    const el = document.createElement("textarea");
    el.value = str;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
  }

  static rand(min, max) {
    return Math.floor(min + Math.random() * (max + 1));
  }

  static generateToken(length) {
    const charset = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
    let token = "";

    for (let i = 0; i < length; ++i)
      token += charset[this.rand(0, charset.length - 1)];

    return token;
  }

  static extractVideoInfo(videoFile) {
    return new Promise(resolve => {
      const info = { thumb: null, duration: null };

      const video = document.createElement("video");
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      video.onloadedmetadata = () => {
        const middleTime = video.duration / 2;
        video.currentTime = middleTime;

        video.onseeked = () => {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
          video.pause();
          canvas.toBlob(blob => {
            info.thumb = blob;
            info.duration = video.duration;
            resolve(info);
          }, "image/jpeg");
        };
      };

      if (videoFile.name.split(".").pop().toLowerCase() != "mp4") {
        resolve(info);
      }

      video.src = URL.createObjectURL(videoFile);

      video.load();
    });
  }

  static msToTime(ms, includeDecimals = false) {
    if (ms < 0) return "00:00";

    ms = Big(ms);
    const seconds = ms.div(1000).round(0, Big.roundDown);
    const hours = seconds.div(3600).round(0, Big.roundDown);
    const minutes = seconds.mod(3600).div(60).round(0, Big.roundDown);
    const secondsRemaining = seconds.mod(60);

    let str = "";
    if (hours > 0) {
      str = `${hours.toString()}:${this.padZero(minutes.toString())}:${this.padZero(secondsRemaining.toString())}`;
    } else
      str = `${this.padZero(minutes.toString())}:${this.padZero(secondsRemaining.toString())}`;

    if (includeDecimals) {
      const decimals = ms.div(1000).toFixed(3).toString().split(".")[1];
      str += `.${decimals}`;
    }

    return str;
  }

  static msToTimeAndFrames(ms, fps = 25) {
    if (isNaN(ms) || ms < 0) return "00:00:00";

    ms = Big(ms);
    const totalSeconds = ms.div(1000).round(0, Big.roundDown);
    const hours = totalSeconds.div(3600).round(0, Big.roundDown);
    const minutes = totalSeconds.mod(3600).div(60).round(0, Big.roundDown);
    const secondsRemaining = totalSeconds.mod(60);

    // Calculate frames based on fps
    const frames = ms.mod(1000).mul(fps).div(1000).round(0, Big.roundDown);

    let str = "";
    if (hours > 0) {
      str = `${hours.toString()}:${this.padZero(minutes.toString())}:${this.padZero(secondsRemaining.toString())}.${this.padZero(frames.toString(), 2)}`;
    } else {
      str = `${this.padZero(minutes.toString())}:${this.padZero(secondsRemaining.toString())}.${this.padZero(frames.toString(), 2)}`;
    }

    return str;
  }

  static sToTimeAndFrames(s, fps = 25) {
    if (isNaN(s) || s < 0) return "00:00:00";

    s = Big(s);
    const totalSeconds = s.round(0, Big.roundDown);
    const hours = totalSeconds.div(3600).round(0, Big.roundDown);
    const minutes = totalSeconds.mod(3600).div(60).round(0, Big.roundDown);
    const secondsRemaining = totalSeconds.mod(60);

    // Calculate frames based on fps
    const frames = s.mod(1).mul(fps).round(0, Big.roundDown);

    let str = "";
    if (hours > 0) {
      str = `${hours.toString()}:${this.padZero(minutes.toString())}:${this.padZero(secondsRemaining.toString())}.${this.padZero(frames.toString(), 2)}`;
    } else {
      str = `${this.padZero(minutes.toString())}:${this.padZero(secondsRemaining.toString())}.${this.padZero(frames.toString(), 2)}`;
    }

    return str;
  }

  static framesToTimeAndFrames(frames, fps = 25) {
    const seconds = frames / fps;
    return this.sToTimeAndFrames(seconds, fps);
  }

  static padZero(value, width = 2) {
    return String(value).padStart(width, "0");
  }

  static formatSeconds(num, includeMinutes = true) {
    const hours = Math.floor(num / 3600);
    let minutes = Math.floor((num % 3600) / 60);
    let seconds = Math.floor(num % 60);

    const parts = [];
    if (hours > 0) parts.push(hours);

    if (minutes < 10) minutes = "0" + minutes;
    if (minutes > 0 || includeMinutes) parts.push(minutes);

    if (seconds < 10) seconds = "0" + seconds;
    parts.push(seconds);

    return parts.join(":");
  }

  static formatNumber(num, decimals = 2) {
    if (!num) return "";

    const locale = localStorage.getItem("user-locale") || "en";
    const format = new Intl.NumberFormat(locale);

    return format.format(num.toFixed(decimals));
  }

  /**
   * Returns a date's time in the format HH:MM:SS (24h)
   * @param {Date} date - The date to extract the time from
   * @returns {string} The formatted time
   */
  static time24h(date, locale) {
    if (!locale) {
      locale = localStorage.getItem("user-locale") || "en";
    }
    return new Date(date).toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    });
  }

  /**
   * Returns the short weekday name for a date (e.g., "lun", "mar", "mié")
   * @param {Date} date - The date to extract the weekday from
   * @param {string} locale - The locale to use (default: "es-ES")
   * @returns {string} The short weekday name
   */
  static shortWeekday(date, locale) {
    if (!locale) {
      locale = localStorage.getItem("user-locale") || "en";
    }
    return new Date(date).toLocaleDateString(locale, {
      weekday: "short"
    });
  }

  /**
   * Checks if two dates are on different days
   * @param {Date} date1 - First date
   * @param {Date} date2 - Second date
   * @returns {boolean} True if dates are on different days
   */
  static areDifferentDays(date1, date2) {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return (
      d1.getFullYear() !== d2.getFullYear() ||
      d1.getMonth() !== d2.getMonth() ||
      d1.getDate() !== d2.getDate()
    );
  }

  static bytesToHuman(bytes) {
    const units = ["bytes", "KB", "MB", "GB", "TB"];
    let unitIndex = 0;

    while (bytes >= 1024 && unitIndex < units.length - 1) {
      bytes /= 1024;
      unitIndex++;
    }

    return `${bytes.toFixed(1)} ${units[unitIndex]}`;
  }

  static fileExt(filename) {
    return "." + filename.split(".").pop().toLowerCase();
  }

  static metadataItem(metadata, language, itemName) {
    // First, get the metadata item for the required language
    const metadataItem = metadata.find(t => t.language_id == language);
    if (!metadataItem) return null;

    // Now extract the requested item
    const item = metadataItem.fields?.find(f => f.name == itemName);
    if (!item) return null;

    return item.value;
  }

  static ucfirst(str) {
    str = str.toLocaleLowerCase();
    return str.charAt(0).toLocaleUpperCase() + str.slice(1);
  }

  // Converts values between 0 and 1 to percentages
  static valueToPercentage(value) {
    return Math.round(value * 100);
  }

  static humanizeFileName(str) {
    if (!str) return "";
    // Turn _ and ._ into spaces
    str = str.replace(/_\./g, " ");
    str = str.replace(/_/g, " ");

    // Remove extension
    const parts = str.split(".");
    if (parts.length > 1) {
      parts.pop();
      str = parts.join(".");
    }

    return str;
  }

  static localeIncludes(substring, str, locale = "es") {
    // Function to normalize strings by removing diacritics (accents)
    const normalize = text =>
      text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    // Normalize both the substring and the string for accent-insensitive matching
    const normalizedSubstring = normalize(substring);
    const normalizedStr = normalize(str);

    // Perform a substring search in the normalized string
    return normalizedStr
      .toLocaleLowerCase(locale)
      .includes(normalizedSubstring.toLocaleLowerCase(locale));
  }

  static clipboardText(element) {
    return Array.from(element.childNodes)
      .map(node => {
        // Some elements will have special values for clipboard copy.
        if (typeof node.dataset?.clipboardText != "undefined")
          return node.dataset.clipboardText;

        return node.nodeType === Node.TEXT_NODE
          ? node.textContent.trim()
          : this.clipboardText(node);
      })
      .filter(text => text)
      .join("\n");
  }

  static getContrastColor(hex) {
    // Ensure the input is a valid 6-character hex color
    hex = hex.replace(/^#/, "");
    if (hex.length === 3) {
      // Convert shorthand hex to full
      hex = hex
        .split("")
        .map(c => c + c)
        .join("");
    }

    const r = parseInt(hex.slice(0, 2), 16) / 255; // Red
    const g = parseInt(hex.slice(2, 4), 16) / 255; // Green
    const b = parseInt(hex.slice(4, 6), 16) / 255; // Blue

    // Calculate relative luminance
    const luminance = c => {
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };

    const relativeLuminance =
      0.2126 * luminance(r) + 0.7152 * luminance(g) + 0.0722 * luminance(b);

    // Return white (#FFFFFF) if the luminance is low, black (#000000) if it's high
    return relativeLuminance > 0.5 ? "#000000" : "#FFFFFF";
  }

  // Create a function that accepts a #RRGGBB and a 0-1 opacity value and returns the color property of CSS
  static rgbaToCss(hex, opacity) {
    hex = hex.replace(/^#/, "");
    // Check if the color is #RGB
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map(c => c + c)
        .join("");
    }
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  static daysInMonth(year, month) {
    return new Date(year, month, 0).getDate();
  }

  static roundDecimals(number, numDecimals) {
    if (number % 1 !== 0) return number.toFixed(numDecimals);

    return number;
  }

  static speakersColorPalette = [
    "#BAFFC9",
    "#BAE1FF",
    "#FFFFBA",
    "#FFE4B5",
    "#E0BBE4",
    "#957DAD",
    "#D4A5A5",
    "#FFB3BA",
    "#9DC88D",
    "#A7C7E7",
    "#F1C0E8",
    "#CFBAF0",
    "#B8E0D2",
    "#FAC898",
    "#ECD5E3",
    "#FF9AA2",
    "#B5EAD7",
    "#C7CEEA",
    "#FFDAC1",
    "#E2F0CB"
  ];

  static rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    return [h, s, l];
  }

  static hslToRgb(h, s, l) {
    let r, g, b;

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  }

  static hexToRgb(hex) {
    hex = hex.replace(/^#/, "");
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map(c => c + c)
        .join("");
    }
    const int = parseInt(hex, 16);
    return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
  }

  static rgbToHex(r, g, b) {
    return "#" + [r, g, b].map(x => x.toString(16).padStart(2, "0")).join("");
  }

  static textColor(bgColor) {
    if (!bgColor) return "#333";

    let [r, g, b] = this.hexToRgb(bgColor);
    const [h, s] = this.rgbToHsl(r, g, b);

    [r, g, b] = this.hslToRgb(h, s, 0.15);
    return this.rgbToHex(r, g, b);
  }

  static isValidHexColor(color) {
    const colorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return colorRegex.test(color);
  }

  static parseVtt(vttContent) {
    const cues = [];
    const lines = vttContent.split("\n");
    const speakersColors = {};

    // Speakers colors
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line.includes("::cue")) continue;

      // From a line content like ::cue(.speaker_682) {color: yellow;} extract
      // the speaker id and the color name
      const speakerId = line.match(/speaker_(\w+)/)?.[1];
      const colorName = line.match(/color: (\w+)/)?.[1];
      if (speakerId && colorName) {
        speakersColors[speakerId] = colorName;
      }
    }

    // Text lines
    let i = 0;
    while (i < lines.length) {
      const line = lines[i].trim();

      // Search for time lines (format: 00:00:25.905 --> 00:00:30.079)
      if (line.includes(" --> ")) {
        const [startTime, endTime] = line.split(" --> ");
        const start = this.timeToSeconds(startTime);
        const end = this.timeToSeconds(endTime);

        i++;
        const textLines = [];
        let speaker = "";

        // Get text lines until an empty line or the next cue is found
        while (
          i < lines.length &&
          lines[i].trim() !== "" &&
          !lines[i].includes(" --> ")
        ) {
          const textLine = lines[i].trim();

          // Extract speaker if exists and clean text
          if (textLine.includes("<c.speaker_")) {
            const speakerMatch = textLine.match(/<c\.speaker_([a-f0-9-]+)>/);
            if (speakerMatch && !speaker) {
              speaker = speakerMatch[1];
            }

            // Clean speaker tags from text
            const cleanText = textLine.replace(/<c\.speaker_[^>]+>|<\/c>/g, "");
            if (cleanText.trim()) {
              textLines.push(cleanText.trim());
            }
          } else if (textLine && !textLine.includes("<c.sound_description>")) {
            textLines.push(textLine);
          }
          i++;
        }

        if (textLines.length > 0) {
          // Join lines with line breaks to preserve original format
          const text = textLines.join("\n");
          cues.push({
            start,
            end,
            text,
            color: speakersColors[speaker] || undefined
          });
        }
      } else {
        i++;
      }
    }

    return cues;
  }

  static timeToSeconds(timeString) {
    const parts = timeString.split(":");
    const hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);
    const seconds = parseFloat(parts[2]);

    return hours * 3600 + minutes * 60 + seconds;
  }

  static formatDate({ date, language, options }) {
    const locale = language || localStorage.getItem("user-locale") || "en";

    if (typeof date === "string") {
      date = new Date(date);
    }

    if (options === "full") {
      options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "numeric"
      };
    }

    return new Intl.DateTimeFormat([locale], options).format(date);
  }

  static formatDateAgo({ date, language }) {
    if (!language) {
      language = localStorage.getItem("user-locale") || "en";
    }
    if (typeof date === "string") {
      date = new Date(date);
    }

    const rtf = new Intl.RelativeTimeFormat(language, { numeric: "auto" });
    const now = new Date();
    const diffInSeconds = Math.floor((date - now) / 1000);

    const divisions = [
      { amount: 60, name: "seconds" },
      { amount: 60, name: "minutes" },
      { amount: 24, name: "hours" },
      { amount: 7, name: "days" },
      { amount: 4.34524, name: "weeks" },
      { amount: 12, name: "months" },
      { amount: Number.POSITIVE_INFINITY, name: "years" }
    ];

    let duration = diffInSeconds;
    for (let i = 0; i < divisions.length; i++) {
      const division = divisions[i];
      if (Math.abs(duration) < division.amount) {
        return rtf.format(Math.round(duration), division.name);
      }
      duration /= division.amount;
    }
  }

  static async sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  static isVideoFile(file) {
    const ext = this.fileExt(file.name);
    return this.videoExtensions.includes(ext.toLowerCase());
  }

  static isAudioFile(file) {
    const ext = this.fileExt(file.name);
    return this.audioExtensions.includes(ext.toLowerCase());
  }

  static isImageFile(file) {
    const ext = this.fileExt(file.name);
    return this.imageExtensions.includes(ext.toLowerCase());
  }

  /**
   * Validates if a URL or object URL points to a valid image
   * @param {string} url - The URL or object URL to validate
   * @returns {Promise<boolean>} True if the URL is a valid image, false otherwise
   */
  static async isValidImageUrl(url) {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }

  static isValidDate(date) {
    return date instanceof Date && !isNaN(date);
  }

  /**
   * Parses SRT time info string and converts to milliseconds
   * @param {string} timeStr - Time string in format "HH:MM:SS,mmm --> HH:MM:SS,mmm" or "MM:SS,mmm --> MM:SS,mmm"
   * @returns {Object} Object with start and end in milliseconds
   * @example
   * parseSRTtimeInfo("00:00:03,511 --> 00:00:06,045")
   * // Returns: { start: 3511, end: 6045 }
   */
  static parseSRTtimeInfo(timeStr) {
    const [startStr, endStr] = timeStr.split(" --> ");

    const srtTimeToMs = timeString => {
      // Format: HH:MM:SS,mmm or MM:SS,mmm
      const [timePart, msPart] = timeString.split(",");
      const parts = timePart.split(":").map(Number);

      let hours, minutes, seconds;
      if (parts.length === 3) {
        // HH:MM:SS format
        [hours, minutes, seconds] = parts;
      } else if (parts.length === 2) {
        // MM:SS format (non-standard but handle it)
        hours = 0;
        [minutes, seconds] = parts;
      } else {
        throw new Error(`Invalid SRT time format: ${timeString}`);
      }

      return (
        hours * 3600000 + minutes * 60000 + seconds * 1000 + parseInt(msPart)
      );
    };

    return {
      start: srtTimeToMs(startStr.trim()),
      end: srtTimeToMs(endStr.trim())
    };
  }

  /**
   * Extracts the event ID from a realtime event ID string
   * @param {string} id - The event ID string in format "mc:event:XXXX:Y"
   * @returns {string|null} The event ID (XXXX) or null if the format is invalid
   */
  static realtimeEventFromId(id) {
    if (!id) return null;

    const parts = id.split(":");

    if (parts.length != 4 || parts[0] != "mc" || parts[1] != "event") {
      return null;
    }

    return parts[2];
  }

  /**
   * Calculates times and frames relative to an SMPTE timecode
   * @param {string} start_time - ISO date (e.g., "2026-04-22T15:20:29.546000+00:00")
   * @param {string} end_time - ISO date
   * @param {string} timecode - SMPTE timecode (e.g., "14:59:56:10")
   * @param {number} fps - Frames per second
   * @returns {{ start_ms: number, end_ms: number, start_frame: number, end_frame: number }}
   */
  static calculateRelativeTimeAndFrames(start_time, end_time, timecode, fps) {
    // Parse SMPTE timecode (HH:MM:SS:FF) to milliseconds
    const [hours, minutes, seconds, frames] = timecode.split(":").map(Number);
    const timecodeMs =
      (hours * 3600 + minutes * 60 + seconds) * 1000 +
      Math.round((frames / fps) * 1000);

    // Parse ISO dates and get ms since midnight (UTC)
    const startDate = new Date(start_time);
    const startMsSinceMidnight =
      (startDate.getUTCHours() * 3600 +
        startDate.getUTCMinutes() * 60 +
        startDate.getUTCSeconds()) *
        1000 +
      startDate.getUTCMilliseconds();

    const endDate = new Date(end_time);
    const endMsSinceMidnight =
      (endDate.getUTCHours() * 3600 +
        endDate.getUTCMinutes() * 60 +
        endDate.getUTCSeconds()) *
        1000 +
      endDate.getUTCMilliseconds();

    // Calculate ms relative to timecode
    const start_ms = startMsSinceMidnight - timecodeMs;
    const end_ms = endMsSinceMidnight - timecodeMs;

    // Calculate frames (ms to frames)
    const msPerFrame = 1000 / fps;
    const start_frame = Math.floor(start_ms / msPerFrame);
    const end_frame = Math.floor(end_ms / msPerFrame);

    /*if (start_ms < 0) {
      console.log("OJO");
      console.log({ start_time, end_time, timecode });
    }*/
    return { start_ms, end_ms, start_frame, end_frame };
  }

  /**
   * Finds the closest aspect ratio from a list of aspect ratios
   * @param {number} width - Width of the asset
   * @param {number} height - Height of the asset
   * @returns {string} The closest aspect ratio label
   */
  static closestAspectRatio(width, height) {
    const aspectRatios = useAppStore().aspectRatios.map(r => ({
      label: r.label,
      value:
        r.value.split(":").map(Number)[0] / r.value.split(":").map(Number)[1]
    }));

    const inputRatio = width / height;

    let closest = aspectRatios[0];
    let minDiff = Math.abs(inputRatio - closest.value);

    for (const ratio of aspectRatios) {
      const diff = Math.abs(inputRatio - ratio.value);
      if (diff < minDiff) {
        minDiff = diff;
        closest = ratio;
      }
    }

    return closest.label;
  }
}
